<template>
  <div>
    <el-card class="chart-card">
      <h1>PignooReader的迭代器功能</h1>
      <p>
        <el-text>迭代器是为了遍历大量数据而设计的！</el-text>
      </p>
      <p>
        <el-text>迭代器模式下，过滤条件是生效的，但</el-text>
        <el-text tag="b">排序条件是无效的！</el-text>
      </p>
      <p>
        <el-text>迭代器是不加“写锁”的！</el-text>
      </p>
      <h1>Reader与Writer</h1>
      <p>
        <el-text>PignooReader与PignooWriter均有iterator方法。都不会额外增加行锁。但是</el-text>
        <el-text tag="b">PignooWriter遍历出的对象会使用setter代理！</el-text>
      </p>
      <h1>迭代器原理</h1>
      <p>
        <el-text tag="b">前排提示：</el-text>
        <el-text>迭代器步长默认100，是综合各方面考虑的结果。</el-text>
        <el-text>如果遍历的数据包含大字段，请设置一个小的步长，防止内存溢出；</el-text>
        <el-text>如果遍历的数据行很小，但总量很大，请设置一个大的步长，以提升性能。</el-text>
        <el-text>不要将步长100作为魔法变量去使用，后续可能进行做算法调整，或动态分配步长。</el-text>
      </p>
      <p>
        <el-text>在我们需要迭代大量数据时，比如在上百万数据的表中筛选出满足条件的数据并做指定更新时，这种操作往往面临几个问题：</el-text>
        <br />
        <el-text>1. 深分页问题：当类似MySQL LIMIT分页这种方式，到达比较深的页码时，每次查询都会非常消耗资源。</el-text>
        <br />
        <el-text>2. 内存溢出问题：如果一次性加载很多数据到内存，会出现内存溢出，所以需要分配处理。</el-text>
        <br />
        <el-text>3. 总耗时太长：如果在事务中执行，且加写锁，会长时间锁表，导致其他业务无法进行。</el-text>
      </p>
      <p>
        <el-text>最常见解决方式是：索引分段式查询 + 读表不加锁的方案，且不推荐在事务环境下执行大数据量遍历。Pignoo的迭代器就是采用这个方案（ID分段式查询）解决的大数据量表遍历的问题的。</el-text>
        <br />
        <el-text>例如：查询出满足条件的、ID最小的前100个，下次查询ID大于上次最大ID的前100个，由此往复。</el-text>
        <el-text tag="b">其中，“100”我们称之为“迭代器查询步长”，简称“步长”。</el-text>
      </p>
      <p>
        <el-text>PignooReader通过继承Iterable接口，以迭代器的形式实现了这种大量数据遍历的方案，使得Pignoo遍历全表变得非常简单：</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code1 }}</code></pre>
      </div>
      <p>
        <el-text>再次强调，因为采用了ID分段查询，所以不支持排序条件！</el-text>
      </p>
      <h1>迭代器的其他用法</h1>
      <p>
        <el-text tag="b">设置步长、排序方式，再遍历</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code2 }}</code></pre>
      </div>
      <p>
        <el-text tag="b">非forEach循环的用法</el-text>
      </p>
      <p>
        <el-text>非forEach循环，使用迭代器可以再多设置2个参数：offset（跳过个数）、limit（最大遍历个数）。</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code3 }}</code></pre>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'

onMounted(() => {
  if (window.hljs) {
    window.hljs.highlightAll()
  }
})

const code1 = `var writer = pignoo.writer(Pig.class);
for(Pig pig : writer) { // 如此遍历，内部执行时会进行分段查询，不用担心内存溢出，非常简洁！
  pig.setName(pig.getName() + "宝宝");
}`

const code2 = `var writer = pignoo.writer(Pig.class);

writer.setIteratorStep(20); // 设置步长为20：每次查询20条数据，遍历完成再查询后20条...
writer.setIteratorSortMode(SMode.MAX_FIRST); // 设置从后向前遍历（不支持排序条件，但之“从前到后”或“从后到前”）

for(Pig pig : writer) {
  pig.setName(pig.getName() + "宝宝");
}`

const code3 = `var writer = pignoo.writer(Pig.class);

// 每次查询20个，从后向前，跳过前1000个，最多查询30000个
Iterator<Pig> iterator = writer.iterator(20, SMode.MAX_FIRST, 1000L, 30000L);
while (iterator.hasNext()) {
  Pig pig = iterator.next();
  pig.setName(pig.getName() + "宝宝");
}`

</script>

<style lang="less"></style>